// src/data/projectTravelClient.js
//
// Thin client for /api/project-travel. Stateless; no caching. Server errors
// are sanitized ({ error }) and surfaced verbatim via errorFromResponse.
// No travel data is logged (only status / error class names).
//
//   getTravelOptions()            GET    /api/project-travel/options
//   getTravelMonth(params)        GET    /api/project-travel?month&tz&filters
//   getTrip(id)                   GET    /api/project-travel/:id
//   getUpcomingTrips(projectId)   GET    /api/project-travel/upcoming?projectId
//   createTrip(body)              POST   /api/project-travel
//   updateTrip(id, body)          PUT    /api/project-travel/:id
//   deleteTrip(id)                DELETE /api/project-travel/:id (204)

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

const BASE = "/api/project-travel";
export const TRAVEL_FILTER_KEYS = Object.freeze(["travelProject", "travelPerson", "travelMarket", "travelPurpose"]);

async function request(path, { method = "GET", body } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: "include",
      ...(body !== undefined ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    log.error("projectTravelClient: network failure", { method });
    throw new NetworkError("Could not reach the travel service. Check your connection and retry.", err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("projectTravelClient: non-2xx", { method, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

export function getTravelOptions() {
  return request("/options");
}

export function getTravelMonth({ month, tz, filters = {} }) {
  const params = new URLSearchParams({ month, tz });
  for (const key of TRAVEL_FILTER_KEYS) if (filters[key]) params.set(key, filters[key]);
  return request(`?${params}`);
}

export function getTrip(id) {
  return request(`/${encodeURIComponent(id)}`);
}

export async function getUpcomingTrips(projectId) {
  const data = await request(`/upcoming?projectId=${encodeURIComponent(projectId)}`);
  return Array.isArray(data?.trips) ? data.trips : [];
}

export async function createTrip(body) {
  return (await request("", { method: "POST", body }))?.trip ?? null;
}

export async function updateTrip(id, body) {
  return (await request(`/${encodeURIComponent(id)}`, { method: "PUT", body }))?.trip ?? null;
}

export async function deleteTrip(id) {
  await request(`/${encodeURIComponent(id)}`, { method: "DELETE" });
}
